import { useEffect } from 'react';
import {
  Scene,
  MeshBuilder,
  StandardMaterial,
  Texture,
  Color3,
  Mesh,
  Vector3,
  AssetsManager,
  TextureAssetTask,
} from '@babylonjs/core';
import DataManager from '../../manager/DataManager';

type Props = {
  scene: Scene;
};

export default function CartaBackground({ scene }: Props) {
  useEffect(() => {
    if (!scene) return;

    const meshes: Mesh[] = [];

    // ── Chargement des textures via AssetsManager ────────────────────────────
    const assetsManager = new AssetsManager(scene);
    assetsManager.useDefaultLoadingScreen = true;

    const mosaicTask = assetsManager.addTextureTask(
      'Mosaique',
      DataManager.DEFAULT_SCENE_THEME.mosaic
    );
    const floorTask = assetsManager.addTextureTask(
      'Floor',
      DataManager.DEFAULT_SCENE_THEME.floor
    );

    assetsManager.onFinish = () => {
      // ── Mosaic ground ──────────────────────────────────────────────────────
      const mosaic = MeshBuilder.CreateGround(
        'mosaic',
        { width: 30, height: 20, subdivisions: 1 },
        scene
      );
      mosaic.position.y = -0.1;
      mosaic.receiveShadows = true;
      mosaic.isPickable = false;

      const mosaicMat = new StandardMaterial('Mosaic', scene);
      mosaicMat.alpha = 1;
      mosaicMat.specularPower = 3800;
      mosaicMat.specularColor = Color3.White();
      mosaicMat.sideOrientation = Mesh.BACKSIDE;
      mosaicMat.needDepthPrePass = true;
      mosaicMat.backFaceCulling = true;
      mosaicMat.zOffset = 0;

      const mosaicTexture = (mosaicTask as TextureAssetTask).texture;
      mosaicTexture.hasAlpha = true;
      mosaicMat.diffuseTexture = mosaicTexture;
      mosaic.material = mosaicMat;
      meshes.push(mosaic);

      // ── Video ad ground ────────────────────────────────────────────────────
      const videoAd = MeshBuilder.CreateGround(
        'videoad',
        { width: 26.6, height: 16.5, subdivisions: 1 },
        scene
      );
      videoAd.position.y = -0.2;
      videoAd.receiveShadows = true;
      videoAd.isPickable = true;

      const adMat = new StandardMaterial('Ad', scene);
      adMat.alpha = 0;
      adMat.specularPower = 3800;
      adMat.specularColor = Color3.White();
      adMat.sideOrientation = Mesh.BACKSIDE;
      adMat.needDepthPrePass = true;
      adMat.backFaceCulling = true;
      videoAd.material = adMat;
      meshes.push(videoAd);

      // ── Tiled floor ────────────────────────────────────────────────────────
      const res = 60;
      const grid = { h: 4, w: 4 };

      const tiledFloor = MeshBuilder.CreateTiledGround(
        'Floor',
        {
          xmin: -res,
          zmin: -res,
          xmax: res,
          zmax: res,
          subdivisions: { w: grid.w, h: grid.h },
        },
        scene
      );
      tiledFloor.position.y = -5.3;
      tiledFloor.receiveShadows = true;
      tiledFloor.isPickable = false;

      const floorMat = new StandardMaterial('Floor', scene);
      floorMat.alpha = 1;
      floorMat.specularPower = 400;
      floorMat.specularColor = Color3.White();
      floorMat.sideOrientation = Mesh.BACKSIDE;
      floorMat.zOffset = 2;

      const floorTexture = (floorTask as TextureAssetTask).texture;
      floorTexture.hasAlpha = false;
      floorMat.diffuseTexture = floorTexture;
      tiledFloor.material = floorMat;
      meshes.push(tiledFloor);
    };

    assetsManager.load();

    // ── Cleanup ────────────────────────────────────────────────────────────
    return () => {
      meshes.forEach(m => m.dispose());
    };
  }, [scene]);

  return null;
}